/**
 * Formatação de data/hora — padronizada em **GMT-3 (America/Bahia)** em
 * todo o sistema, independentemente do timezone do browser ou do servidor.
 *
 * ⚠️ 11/10/2026 (auditoria de design, DO-05/DO-15; decisão do André): o fuso de exibição de TODOS os
 * produtos da Use é o da BAHIA, `America/Bahia` — antes esta lib dizia `America/Sao_Paulo`. Hoje os
 * dois dão o mesmo horário (UTC-3, sem horário de verão desde 2019), mas o nome certo evita que uma
 * mudança de regra de São Paulo mude o horário da operação da Bahia, e é o que o SAC já usa.
 * "Hoje", "7 dias" e qualquer agregação por dia valem para o DIA CIVIL DA BAHIA, nunca o dia UTC
 * (depois das 21h da Bahia o dia UTC já é o seguinte): use `diaBahia()`.
 *
 * O que esta lib resolve:
 *
 *  1. **Strings UTC sem `Z`** — D1 grava `default current_timestamp` no formato
 *     `"YYYY-MM-DD HH:MM:SS"` (sem `Z`). O construtor `new Date()` interpreta
 *     isso como local-time do browser, criando bug de "criado em > último login"
 *     na mesma vida. Aqui detectamos esse formato e tratamos como UTC.
 *
 *  2. **Timezone do browser não confiável** — operador acessando do exterior
 *     veria horários no fuso dele. Forçamos `America/Bahia` (UTC-3, sem DST
 *     desde 2019) pra que TODOS vejam o mesmo horário de operação.
 *
 *  3. **ISO com `Z`** (ex.: `new Date().toISOString()` do backend) — funciona
 *     normalmente, parse como UTC e convertemos pra BRT.
 *
 * Convenção: backend SEMPRE deveria gravar com `new Date().toISOString()`
 * (ISO Z). Esta lib é defesa em profundidade pra dados legacy do D1.
 */

export const TZ_USE = "America/Bahia";

const FORMATO_DIA = new Intl.DateTimeFormat("en-CA", { timeZone: TZ_USE, year: "numeric", month: "2-digit", day: "2-digit" });

/**
 * Dia civil da Bahia ("AAAA-MM-DD") de um instante. Use para "hoje" e para agrupar/filtrar por dia:
 * `new Date().toISOString().slice(0, 10)` é o dia UTC e vira o dia seguinte às 21h da Bahia.
 */
export function diaBahia(quando: Date | string | number = new Date()): string {
  const d = quando instanceof Date ? quando : typeof quando === "string" ? parseDataRobusto(quando) : new Date(quando);
  return FORMATO_DIA.format(d);
}

/** Instante (ISO UTC) em que o dia civil da Bahia `dia` ("AAAA-MM-DD") começa: 00:00 em UTC-3. */
export function inicioDoDiaBahia(dia: string): string {
  return new Date(`${dia}T00:00:00-03:00`).toISOString();
}

/**
 * Instante (ISO UTC) do começo do dia SEGUINTE ao dia civil da Bahia `dia`: o limite EXCLUSIVO de um
 * filtro "até". Comparar `<= 'AAAA-MM-DDT23:59:59Z'` perde o fim do último dia no fuso da Bahia.
 */
export function fimExclusivoDoDiaBahia(dia: string): string {
  return new Date(new Date(`${dia}T00:00:00-03:00`).getTime() + 86_400_000).toISOString();
}

/**
 * Parse robusto: detecta strings SQLite-style "YYYY-MM-DD HH:MM:SS" sem
 * timezone e adiciona `Z` antes do parse — assume UTC, consistente com
 * `current_timestamp` do SQLite (que é UTC).
 */
function parseDataRobusto(iso: string): Date {
  // Já tem timezone (Z, +HH:MM, -HH:MM no fim)? Usa direto.
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) return new Date(iso);

  // Formato SQLite: "YYYY-MM-DD HH:MM:SS" — converte pra ISO UTC
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(\.\d+)?$/);
  if (m) return new Date(`${m[1]}T${m[2]}${m[3] ?? ""}Z`);

  // Só data, sem hora: trata como início do dia UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return new Date(`${iso}T00:00:00Z`);

  // Fallback: deixa o JS chutar (data inválida vai dar NaN, tratado abaixo)
  return new Date(iso);
}

export function formatarDataHoraBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseDataRobusto(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", {
    timeZone: TZ_USE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Valor de `<input type="datetime-local">` ("AAAA-MM-DDTHH:mm") na hora da BAHIA, a partir de um instante.
 * O campo não tem fuso: ler com `getHours()` do navegador mostrava a hora do computador de quem olha (e
 * gravava a janela de manutenção na hora errada para quem não está na Bahia). Ver `deCampoDataHoraBahia`.
 */
export function paraCampoDataHoraBahia(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : parseDataRobusto(iso);
  if (Number.isNaN(d.getTime())) return "";
  const t = new Intl.DateTimeFormat("sv-SE", {
    timeZone: TZ_USE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d); // "2026-10-09 15:00"
  return t.replace(" ", "T");
}

/**
 * Igual a `paraCampoDataHoraBahia`, COM SEGUNDOS ("AAAA-MM-DDTHH:mm:ss"). O Console Forense precisa do segundo: num IP de
 * CGNAT um minuto traz dezenas de assinantes (medido: 355 registros em 1 min), e reabrir uma busca do histórico tem de
 * cobrir o MESMO instante pesquisado.
 */
export function paraCampoDataHoraSegBahia(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : parseDataRobusto(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: TZ_USE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .format(d)
    .replace(" ", "T");
}

/** Inverso de `paraCampoDataHoraBahia`: o texto digitado é hora da Bahia (UTC-3); devolve ISO UTC, ou "" se inválido. */
export function deCampoDataHoraBahia(local: string): string {
  const m = local.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(:\d{2})?$/);
  if (!m) return "";
  const d = new Date(`${m[1]}T${m[2]}${m[3] ?? ":00"}-03:00`);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/** Mesma data/hora das telas, sem a vírgula do `toLocaleString` ("09/10/2026 15:00"): coluna de CSV. */
export function formatarDataHoraCsv(iso: string | null | undefined): string {
  const t = formatarDataHoraBR(iso);
  return t === "—" ? "" : t.replace(", ", " ");
}

export function formatarDataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseDataRobusto(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-BR", { timeZone: TZ_USE });
}

/**
 * Traduz erros vindos da API forense/infra pra mensagens amigáveis pro
 * operador. Hoje só intercepta rate limit (429); demais erros voltam
 * crus pra debug. Use isso em todo `catch` que mostra erro na UI das
 * páginas /admin/infra/*.
 */
export function formatarErroInfra(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  // Período por calendário (DO-23): a API manda o código e a frase pronta ("periodo_longo: Período longo demais…").
  const periodo = raw.match(/^(periodo_[a-z_]+|agregado_indisponivel): (.+)$/s);
  if (periodo) return periodo[2]!;
  if (/too many requests|rate limit|\b429\b/i.test(raw)) {
    return "A API de logs está com muitas requisições no momento. Aguarde alguns segundos e tente novamente.";
  }
  // 52x do IXC (520-527 = camada Cloudflare↔origin do ERP) — instabilidade
  // momentânea conhecida; o backend já retenta 4x antes de desistir.
  if (/IXC/i.test(raw) && /\b52[0-7]\b/.test(raw)) {
    return "O ERP IXC está instável neste momento (erro de conexão). Clique em Tentar novamente em alguns segundos.";
  }
  return raw;
}

/**
 * Versão completa com segundos — útil pra debug/audit detalhado.
 */
export function formatarDataHoraSegBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseDataRobusto(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", {
    timeZone: TZ_USE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
